import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { IgxGridModule } from '@infragistics/igniteui-angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/core/services/data/data.service';
import { ISendInvoiceNotificationResponse } from 'src/app/shared/interfaces';
import { ProcessingBannerComponent } from 'src/app/shared/ui/processing-banner/processing-banner.component';
import { InfoBannerComponent } from 'src/app/shared/ui/info-banner/info-banner.component';

type SendInvoiceResultsGridRow = ISendInvoiceNotificationResponse & {
  selectedForSend: boolean;
  isEmailed: boolean;
  notificationStatus: 'success' | 'error' | null;
  invoiceNumber: string;
  originalBalance: number;
  emailAddress: string;
  delivery: string;
  dateSent: string;
  IsPortal: boolean | string | number;
};

@Component({
  selector: 'app-send-invoices-results',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatSnackBarModule,
    IgxGridModule,
    ProcessingBannerComponent,
    InfoBannerComponent,
  ],
  templateUrl: './send-invoices-results.component.html',
  styleUrl: './send-invoices-results.component.scss',
})
export class SendInvoicesResultsComponent implements AfterViewInit {
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dataService = inject(DataService);
  private readonly router = inject(Router);
  eligibleInvoiceForEmail: number = 0;
  private _showAllInvoices: boolean = false;
  selectedToEmail: number = 0;
  selectedToSend: number = 0;
  @Input() hasProcessedQuery: boolean = false;
  @Output() quickSearchResultsLoading = new EventEmitter<boolean>();
  @Output() formDisplayed = new EventEmitter<void>();

  private _isQueryInProgress: boolean = false;

  @Input()
  set isQueryInProgress(value: boolean) {
    this._isQueryInProgress = value;
    this.quickSearchResultsLoading.emit(value);
  }

  get isQueryInProgress(): boolean {
    return this._isQueryInProgress;
  }

  private _invoiceSearchResults: ISendInvoiceNotificationResponse[] = [];

  @Input()
  set invoiceSearchResults(value: ISendInvoiceNotificationResponse[]) {
    this._invoiceSearchResults = value ?? [];
    this.quickSearchResults = this._invoiceSearchResults.map((row) => ({
      ...row,
      selectedForSend: (row as Partial<SendInvoiceResultsGridRow>).selectedForSend ?? false,
      isEmailed: (row as Partial<SendInvoiceResultsGridRow>).isEmailed ?? false,
      notificationStatus: (row as Partial<SendInvoiceResultsGridRow>).notificationStatus ?? null,
      invoiceNumber:
        (row as Partial<SendInvoiceResultsGridRow>).invoiceNumber ??
        String((row as ISendInvoiceNotificationResponse).rowNumber ?? ''),
      originalBalance:
        (row as Partial<SendInvoiceResultsGridRow>).originalBalance ??
        (row as ISendInvoiceNotificationResponse).currentBalance ??
        0,
      emailAddress:
        (row as Partial<SendInvoiceResultsGridRow>).emailAddress ??
        (row as ISendInvoiceNotificationResponse).primaryEmailAddress ??
        '',
      delivery:
        (row as Partial<SendInvoiceResultsGridRow>).delivery ??
        (row as ISendInvoiceNotificationResponse).profileDeliverySetting ??
        '',
      dateSent:
        (row as Partial<SendInvoiceResultsGridRow>).dateSent ??
        (row as ISendInvoiceNotificationResponse).dateSend ??
        '',
      IsPortal: (row as Partial<SendInvoiceResultsGridRow>).IsPortal ?? false,
    }));
    this.calculateEligibleInvoiceForEmail();
    this.recomputeDisplayedInvoiceSearchResults();
    this.updateSelectedCounts();
    this.hasCompletedSearch = true;
  }

  get invoiceSearchResults(): ISendInvoiceNotificationResponse[] {
    return this._invoiceSearchResults;
  }

  set showAllInvoices(value: boolean) {
    this._showAllInvoices = value;
    this.recomputeDisplayedInvoiceSearchResults();
  }

  get showAllInvoices(): boolean {
    return this._showAllInvoices;
  }

  quickSearchResults: SendInvoiceResultsGridRow[] = [];
  displayedInvoiceSearchResults: SendInvoiceResultsGridRow[] = [];
  invoiceUserComments: string = '';
  isProcessingNotifications: boolean = false;
  isSendingEmails: boolean = false;
  hasCompletedSearch: boolean = false;
  noSenderEmailAddress: boolean = false;
  currentNotificationName: string = '';
  currentNotificationInvoiceNumber: string = '';
  totalRowsToProcess: number = 0;
  completedRows: number = 0;

  rowStyles = {
    background: (row: any) => {
      const deliverySetting = String(row?.data?.profileDeliverySetting ?? '')
        .trim()
        .toLowerCase();

      const primaryEmailAddress = String(row?.data?.primaryEmailAddress ?? '').trim();
      const numberValidAdditionalEmails = Number(row?.data?.numberValidAdditionalEmails ?? 0);
      const hasPrimaryEmailAddress = primaryEmailAddress.length > 0;
      const hasValidAdditionalEmails = Number.isFinite(numberValidAdditionalEmails) && numberValidAdditionalEmails > 0;

      if (deliverySetting === 'print') {
        return '#f47979';
      }

      if (deliverySetting === 'email' && !hasPrimaryEmailAddress && !hasValidAdditionalEmails) {
        return '#f47979';
      }

      if (deliverySetting === 'email' && (hasPrimaryEmailAddress || hasValidAdditionalEmails)) {
        return '#fefefe';
      }

      return null;
    },
  };

  get selectedRows(): number {
    return this.selectedToSend;
  }

  get emailedCount(): number {
    return this.quickSearchResults.filter((row) => row.isEmailed).length;
  }

  get sendProgressValue(): number {
    if (!this.totalRowsToProcess) {
      return 0;
    }

    return (this.completedRows / this.totalRowsToProcess) * 100;
  }

  onSelectedForSendChange(row: SendInvoiceResultsGridRow, checked: boolean): void {
    row.selectedForSend = checked;
    this.updateSelectedCounts();
  }

  selectAllToEmail(): void {
    this.quickSearchResults.forEach((row) => {
      if (!row.isEmailed && row.eligibleForEmail) {
        row.selectedForSend = true;
      }
    });
    this.updateSelectedCounts();
  }

  clearAllToEmail(): void {
    this.quickSearchResults.forEach((row) => {
      if (!row.isEmailed) {
        row.selectedForSend = false;
      }
    });
    this.updateSelectedCounts();
  }

  calculateEligibleInvoiceForEmail(): void {
    let eligbleCount = 0;
    let ii: number = 0;

    for (ii = 0; ii < this.quickSearchResults.length; ii++) {
      const row = this.quickSearchResults[ii];
      row.eligibleForEmail = false;
      if (row.profileDeliverySetting?.toLowerCase() === 'email') {
        if (row.primaryEmailAddress != '' || row.numberValidAdditionalEmails > 0) {
          row.eligibleForEmail = true;
          eligbleCount++;
        }
      }
    }

    this.eligibleInvoiceForEmail = eligbleCount;
  }

  onDisplayInvoice(invoiceNumber: string, lotNumber: string, entityId: string): void {
    const safeEntityId = entityId && entityId.trim().length > 0 ? entityId : 'na';
    const invoiceViewUrl = this.router.serializeUrl(
      this.router.createUrlTree(['/viewinvoice', safeEntityId, lotNumber, invoiceNumber]),
    );
    window.open(invoiceViewUrl, '_blank');
  }

  async onSendInvoices(): Promise<void> {
    const selectedRows = this.quickSearchResults.filter((row) => row.selectedForSend && !row.isEmailed);

    if (!selectedRows.length) {
      return;
    }

    this.isProcessingNotifications = true;
    this.isSendingEmails = true;
    this.totalRowsToProcess = selectedRows.length;
    this.completedRows = 0;

    try {
      for (const row of selectedRows) {
        this.currentNotificationName = row.customerName ?? '';
        this.currentNotificationInvoiceNumber = row.invoiceNumber ?? '';

        const sendSucceeded = await this.onSendInvoice(row.invoiceNumber ?? '');
        row.notificationStatus = sendSucceeded ? 'success' : 'error';
        row.isEmailed = sendSucceeded;
        this.completedRows += 1;

        if (sendSucceeded) {
          row.selectedForSend = false;
          row.dateSent = new Date().toISOString();
          this.onUpdateInvoiceSendDate(row.invoiceNumber ?? '');
          this.quickSearchResults = [...this.quickSearchResults];
          this.recomputeDisplayedInvoiceSearchResults();
          this.updateSelectedCounts();
        }
      }

      this.snackBar.open('Finished sending invoice notification emails.', 'Close', {
        duration: 5000,
        panelClass: ['send-invoices-complete-snackbar'],
        verticalPosition: 'top',
        horizontalPosition: 'center',
      });
    } finally {
      this.isProcessingNotifications = false;
      this.isSendingEmails = false;
    }
  }

  private async onSendInvoice(invoiceNumber: string): Promise<boolean> {
    try {
      const status = await firstValueFrom(
        this.dataService
          .sendInvoiceNotificationEmailWithComments(invoiceNumber, this.invoiceUserComments)
          .pipe(takeUntilDestroyed(this.destroyRef)),
      );

      return status;
    } catch {
      return false;
    }
  }

  private onUpdateInvoiceSendDate(invoiceNumber: string): void {
    const dateSent = new Date();
    this.dataService
      .updateInvoiceNotificationEmailDateSent(invoiceNumber, dateSent.toISOString().split('T')[0])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private updateSelectedCounts(): void {
    const count = this.quickSearchResults.filter((row) => row.selectedForSend === true).length;
    this.selectedToEmail = count;
    this.selectedToSend = count;
  }

  private recomputeDisplayedInvoiceSearchResults(): void {
    this.displayedInvoiceSearchResults = this.showAllInvoices
      ? [...this.quickSearchResults]
      : this.quickSearchResults.filter((row) => row.eligibleForEmail === true);
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.formDisplayed.emit());
  }
}
